// src/api/menus.js
import { apiFetch } from './index.js';
import { toTitleCase } from '@/utils/stringUtils.js';

const getProp = (obj, propName) => {
  if (!obj) return undefined;
  const keys = Object.keys(obj);
  const normalizedProp = propName.toLowerCase().replace(/_/g, '');
  const foundKey = keys.find(k => k.toLowerCase().replace(/_/g, '') === normalizedProp);
  return foundKey ? obj[foundKey] : undefined;
};

const unwrapSingle = (res) => {
  if (!res) return null;
  if (res.data && !Array.isArray(res.data) && typeof res.data === 'object') return res.data;
  return res;
};

const unwrapList = (res) => {
  if (!res) return [];
  if (res.data && Array.isArray(res.data)) return res.data;
  if (Array.isArray(res)) return res;
  return [];
};

// ============ MENU ============
export const convertirMenuDesdeAPI = (raw) => {
  const source = unwrapSingle(raw);
  if (!source) return null;
  const id = source.idMenu ?? source.IdMenu ?? getProp(source, 'idMenu') ?? source.id;
  return {
    idMenu: id, id,
    nombre: toTitleCase(source.nombre || source.Nombre || ''),
    descripcion: source.descripcion || source.Descripcion || '',
    fecha_inicio: source.fecha_inicio || source.fechaInicio || source.FechaInicio || null,
    fecha_fin: source.fecha_fin || source.fechaFin || source.FechaFin || null,
    estado: (source.estado || source.Estado || 'activo').toLowerCase(),
    es_principal: source.es_principal ?? source.esPrincipal ?? source.EsPrincipal ?? false,
    fecha_creacion: source.fecha_creacion || source.fechaCreacion || source.FechaCreacion || null,
    TotalCategorias: source.TotalCategorias ?? source.totalCategorias ?? 0,
    TotalProductos: source.TotalProductos ?? source.totalProductos ?? 0,
    ...source, idMenu: id,
  };
};

export const convertirMenuParaAPI = (f) => ({
  nombre: toTitleCase((f.nombre || '').trim()),
  descripcion: f.descripcion || null,
  fecha_inicio: f.fecha_inicio || null,
  fecha_fin: f.fecha_fin || null,
  estado: f.estado || 'activo',
  es_principal: !!f.es_principal,
});

// ============ CATEGORIA ============
export const convertirCategoriaDesdeAPI = (raw) => {
  const source = unwrapSingle(raw);
  if (!source) return null;
  const id = source.idMenuCategoria ?? source.IdMenuCategoria ?? getProp(source, 'idMenuCategoria') ?? source.id;
  return {
    idMenuCategoria: id, id,
    idMenu: source.idMenu ?? source.IdMenu ?? null,
    nombreMenu: toTitleCase(source.nombreMenu || source.NombreMenu || ''),
    nombre: toTitleCase(source.nombre || source.Nombre || ''),
    descripcion: source.descripcion || source.Descripcion || '',
    icono: source.icono || source.Icono || '',
    orden: source.orden ?? source.Orden ?? 0,
    estado: (source.estado || source.Estado || 'activo').toLowerCase(),
    TotalProductos: source.TotalProductos ?? source.totalProductos ?? 0,
    ...source, idMenuCategoria: id,
  };
};

export const convertirCategoriaParaAPI = (c) => ({
  idMenu: Number(c.idMenu) || 0,
  nombre: toTitleCase((c.nombre || '').trim()),
  descripcion: c.descripcion || null,
  icono: c.icono || null,
  orden: Number(c.orden) || 0,
  estado: c.estado || 'activo',
});

// ============ PRODUCTO ============
export const convertirProductoDesdeAPI = (raw) => {
  const source = unwrapSingle(raw);
  if (!source) return null;
  const id = source.idMenuProducto ?? source.IdMenuProducto ?? getProp(source, 'idMenuProducto') ?? source.id;
  return {
    idMenuProducto: id, id,
    idMenuCategoria: source.idMenuCategoria ?? source.IdMenuCategoria ?? null,
    nombreCategoria: toTitleCase(source.nombreCategoria || source.NombreCategoria || ''),
    idProducto: source.idProducto ?? source.IdProducto ?? null,
    nombreProducto: toTitleCase(source.nombreProducto || source.NombreProducto || ''),
    idAreaCocina: source.idAreaCocina ?? source.IdAreaCocina ?? null,
    nombreArea: toTitleCase(source.nombreArea || source.NombreArea || ''),
    precio_especial_usd: source.precio_especial_usd ?? source.precioEspecialUsd ?? source.PrecioEspecialUsd ?? null,
    precio_especial_bs: source.precio_especial_bs ?? source.precioEspecialBs ?? source.PrecioEspecialBs ?? null,
    disponible: source.disponible ?? source.Disponible ?? true,
    es_destacado: source.es_destacado ?? source.esDestacado ?? source.EsDestacado ?? false,
    orden: source.orden ?? source.Orden ?? 0,
    descripcion_corta: source.descripcion_corta || source.descripcionCorta || source.DescripcionCorta || '',
    fecha_creacion: source.fecha_creacion || source.fechaCreacion || source.FechaCreacion || null,
    TotalModificadores: source.TotalModificadores ?? source.totalModificadores ?? 0,
    ...source, idMenuProducto: id,
  };
};

export const convertirProductoParaAPI = (p) => ({
  idMenuCategoria: Number(p.idMenuCategoria) || 0,
  idProducto: Number(p.idProducto) || 0,
  idAreaCocina: p.idAreaCocina ? Number(p.idAreaCocina) : null,
  precio_especial_usd: p.precio_especial_usd !== '' && p.precio_especial_usd != null ? Number(p.precio_especial_usd) : null,
  precio_especial_bs: p.precio_especial_bs !== '' && p.precio_especial_bs != null ? Number(p.precio_especial_bs) : null,
  disponible: !!p.disponible,
  es_destacado: !!p.es_destacado,
  orden: Number(p.orden) || 0,
  descripcion_corta: p.descripcion_corta || null,
});

// ============ HELPERS UI ============
export const formatearFecha = (iso, completo = false) => {
  if (!iso) return 'N/A';
  try {
    const d = new Date(iso);
    const opt = { year: 'numeric', month: 'short', day: 'numeric' };
    if (completo) { opt.hour = '2-digit'; opt.minute = '2-digit'; }
    return d.toLocaleDateString('es-ES', opt);
  } catch { return iso; }
};

export const convertirEstadoATexto = (estado) => {
  if (!estado) return 'Sin estado';
  return estado.charAt(0).toUpperCase() + estado.slice(1);
};

// ================= API CALLS =================

// MENU
export const obtenerMenus = async () => {
  try {
    const r = await apiFetch('/MenuRestaurante');
    const list = unwrapList(r).map(convertirMenuDesdeAPI).filter(Boolean);
    return { success: true, data: list, count: list.length };
  } catch (e) { return { success: false, error: e.message, data: [] }; }
};

export const obtenerMenuPorId = async (id) => {
  try {
    const r = await apiFetch(`/MenuRestaurante/${id}`);
    return { success: true, data: convertirMenuDesdeAPI(r) };
  } catch (e) { return { success: false, error: e.message, data: null }; }
};

export const obtenerMenuConRelaciones = async (id) => {
  try {
    const r = await apiFetch(`/MenuRestaurante/${id}/conRelaciones`);
    return { success: true, data: convertirMenuDesdeAPI(r) };
  } catch (e) { return { success: false, error: e.message, data: null }; }
};

export const buscarMenus = async (criterios = {}) => {
  try {
    const r = await apiFetch('/MenuRestaurante/search', {
      method: 'POST',
      body: JSON.stringify({
        nombre: criterios.nombre || null,
        estado: criterios.estado || null,
        es_principal: criterios.es_principal ?? null,
      }),
    });
    const list = unwrapList(r).map(convertirMenuDesdeAPI).filter(Boolean);
    return { success: true, data: list };
  } catch (e) { return { success: false, error: e.message, data: [] }; }
};

export const crearMenu = async (m) => {
  try {
    if (!m.nombre || !m.nombre.trim()) throw new Error('El nombre es requerido');
    const r = await apiFetch('/MenuRestaurante', {
      method: 'POST',
      body: JSON.stringify(convertirMenuParaAPI(m)),
    });
    return { success: true, data: convertirMenuDesdeAPI(r) };
  } catch (e) { return { success: false, error: e.message, data: null }; }
};

export const actualizarMenu = async (id, m) => {
  try {
    if (!m.nombre || !m.nombre.trim()) throw new Error('El nombre es requerido');
    const r = await apiFetch(`/MenuRestaurante/${id}`, {
      method: 'PUT',
      body: JSON.stringify(convertirMenuParaAPI(m)),
    });
    return { success: true, data: convertirMenuDesdeAPI(r) };
  } catch (e) { return { success: false, error: e.message, data: null }; }
};

export const eliminarMenu = async (id) => {
  try {
    const r = await apiFetch(`/MenuRestaurante/${id}`, { method: 'DELETE' });
    return { success: true, data: r };
  } catch (e) { return { success: false, error: e.message, data: null }; }
};

// CATEGORIA
export const obtenerCategorias = async () => {
  try {
    const r = await apiFetch('/MenuRestaurante/categorias');
    return { success: true, data: unwrapList(r).map(convertirCategoriaDesdeAPI).filter(Boolean) };
  } catch (e) { return { success: false, error: e.message, data: [] }; }
};

export const obtenerCategoriasPorMenu = async (idMenu) => {
  try {
    const r = await apiFetch(`/MenuRestaurante/categorias/porMenu/${idMenu}`);
    return { success: true, data: unwrapList(r).map(convertirCategoriaDesdeAPI).filter(Boolean) };
  } catch (e) { return { success: false, error: e.message, data: [] }; }
};

export const obtenerCategoriaPorId = async (id) => {
  try {
    const r = await apiFetch(`/MenuRestaurante/categorias/${id}`);
    return { success: true, data: convertirCategoriaDesdeAPI(r) };
  } catch (e) { return { success: false, error: e.message, data: null }; }
};

export const crearCategoria = async (c) => {
  try {
    if (!c.nombre || !c.nombre.trim()) throw new Error('El nombre es requerido');
    const r = await apiFetch('/MenuRestaurante/categorias', {
      method: 'POST',
      body: JSON.stringify(convertirCategoriaParaAPI(c)),
    });
    return { success: true, data: convertirCategoriaDesdeAPI(r) };
  } catch (e) { return { success: false, error: e.message, data: null }; }
};

export const actualizarCategoria = async (id, c) => {
  try {
    const r = await apiFetch(`/MenuRestaurante/categorias/${id}`, {
      method: 'PUT',
      body: JSON.stringify(convertirCategoriaParaAPI(c)),
    });
    return { success: true, data: convertirCategoriaDesdeAPI(r) };
  } catch (e) { return { success: false, error: e.message, data: null }; }
};

export const eliminarCategoria = async (id) => {
  try {
    const r = await apiFetch(`/MenuRestaurante/categorias/${id}`, { method: 'DELETE' });
    return { success: true, data: r };
  } catch (e) { return { success: false, error: e.message, data: null }; }
};

// PRODUCTO
export const obtenerProductos = async () => {
  try {
    const r = await apiFetch('/MenuRestaurante/productos');
    return { success: true, data: unwrapList(r).map(convertirProductoDesdeAPI).filter(Boolean) };
  } catch (e) { return { success: false, error: e.message, data: [] }; }
};

export const obtenerProductosPorCategoria = async (idCategoria) => {
  try {
    const r = await apiFetch(`/MenuRestaurante/productos/porCategoria/${idCategoria}`);
    return { success: true, data: unwrapList(r).map(convertirProductoDesdeAPI).filter(Boolean) };
  } catch (e) { return { success: false, error: e.message, data: [] }; }
};

export const buscarProductos = async (criterios = {}) => {
  try {
    const r = await apiFetch('/MenuRestaurante/productos/search', {
      method: 'POST',
      body: JSON.stringify({
        idMenuCategoria: criterios.idMenuCategoria || null,
        idProducto: criterios.idProducto || null,
        idAreaCocina: criterios.idAreaCocina || null,
        disponible: criterios.disponible ?? null,
        es_destacado: criterios.es_destacado ?? null,
      }),
    });
    return { success: true, data: unwrapList(r).map(convertirProductoDesdeAPI).filter(Boolean) };
  } catch (e) { return { success: false, error: e.message, data: [] }; }
};

export const crearProducto = async (p) => {
  try {
    const r = await apiFetch('/MenuRestaurante/productos', {
      method: 'POST',
      body: JSON.stringify(convertirProductoParaAPI(p)),
    });
    return { success: true, data: convertirProductoDesdeAPI(r) };
  } catch (e) { return { success: false, error: e.message, data: null }; }
};

export const actualizarProducto = async (id, p) => {
  try {
    const r = await apiFetch(`/MenuRestaurante/productos/${id}`, {
      method: 'PUT',
      body: JSON.stringify(convertirProductoParaAPI(p)),
    });
    return { success: true, data: convertirProductoDesdeAPI(r) };
  } catch (e) { return { success: false, error: e.message, data: null }; }
};

export const cambiarDisponibilidadProducto = async (id, disponible) => {
  try {
    const r = await apiFetch(`/MenuRestaurante/productos/${id}/disponibilidad?disponible=${disponible}`, {
      method: 'PUT',
    });
    return { success: true, data: convertirProductoDesdeAPI(r) };
  } catch (e) { return { success: false, error: e.message, data: null }; }
};

export const eliminarProducto = async (id) => {
  try {
    const r = await apiFetch(`/MenuRestaurante/productos/${id}`, { method: 'DELETE' });
    return { success: true, data: r };
  } catch (e) { return { success: false, error: e.message, data: null }; }
};

export default {
  // MENU
  obtenerMenus, obtenerMenuPorId, obtenerMenuConRelaciones, buscarMenus,
  crearMenu, actualizarMenu, eliminarMenu,
  // CATEGORIA
  obtenerCategorias, obtenerCategoriasPorMenu, obtenerCategoriaPorId,
  crearCategoria, actualizarCategoria, eliminarCategoria,
  // PRODUCTO
  obtenerProductos, obtenerProductosPorCategoria, buscarProductos,
  crearProducto, actualizarProducto, cambiarDisponibilidadProducto, eliminarProducto,
  // HELPERS
  formatearFecha, convertirEstadoATexto,
  convertirMenuDesdeAPI, convertirMenuParaAPI,
  convertirCategoriaDesdeAPI, convertirCategoriaParaAPI,
  convertirProductoDesdeAPI, convertirProductoParaAPI,
};